import React, { useEffect, useRef, useState } from 'react';
import { getFieldSelectOptions } from '../../config/adminFieldOptions';
import {
  formatMoneyInputValue,
  isMoneyField,
  roundMoney,
} from '../../config/adminEntities';

const InlineFieldEditor = ({
  field,
  value,
  onSave,
  onCancel,
  departments = [],
  roles = [],
  designations = [],
  treatmentTypes = [],
  disabled = false,
}) => {
  const moneyField = isMoneyField(field.name, field);

  const toLocal = (val) => {
    if (moneyField) return formatMoneyInputValue(val);
    return val ?? '';
  };

  const [localValue, setLocalValue] = useState(() => toLocal(value));
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalValue(toLocal(value));
  }, [value, moneyField]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select && field.type !== 'select') {
        inputRef.current.select();
      }
    }
  }, [field.type]);

  const commit = (advance) => {
    if (disabled) return;
    let out = localValue;
    if (moneyField) {
      const rounded = roundMoney(localValue);
      if (rounded === null) return;
      out = rounded;
      setLocalValue(rounded.toFixed(2));
    }
    onSave(out, advance);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit('down');
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      commit(e.shiftKey ? 'prev' : 'next');
    }
  };

  const baseClass =
    'w-full min-w-[72px] rounded border border-[#E4C984] bg-white px-1.5 py-0.5 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-[#E4C984]';

  if (field.type === 'checkbox') {
    return (
      <input
        ref={inputRef}
        type="checkbox"
        className="h-4 w-4 rounded border-gray-300 text-[#E4C984] focus:ring-[#E4C984]"
        checked={Boolean(localValue)}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.checked;
          setLocalValue(next);
          onSave(next);
        }}
      />
    );
  }

  if (field.type === 'select') {
    const options = getFieldSelectOptions(field, {
      departments,
      roles,
      designations,
      treatmentTypes,
      currentValue: value,
    });
    return (
      <select
        ref={inputRef}
        className={baseClass}
        value={localValue ?? ''}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value;
          setLocalValue(next);
          onSave(next);
        }}
        onKeyDown={handleKeyDown}
      >
        <option value="">—</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    );
  }

  if (moneyField) {
    return (
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        className={baseClass}
        value={localValue ?? ''}
        disabled={disabled}
        placeholder={field.placeholder || '0.00'}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={() => commit()}
        onKeyDown={handleKeyDown}
      />
    );
  }

  return (
    <input
      ref={inputRef}
      type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'email' ? 'email' : 'text'}
      className={baseClass}
      value={localValue ?? ''}
      disabled={disabled}
      placeholder={field.placeholder}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => commit()}
      onKeyDown={handleKeyDown}
    />
  );
};

export default InlineFieldEditor;
